import { useQuery } from "@tanstack/react-query";
import type { ScenarioAuditLogEntry } from "../api/models/scenarioAuditLogView";
import { getScenarioAuditLogGraphql } from "../api/services/scenarioGraphqlService";
import { useApp } from "../contexts/AppContext";
import type {
  ScenarioAuditEntry,
  ScenarioAuditEventType,
} from "../types/scenarios";

export interface ScenarioAuditLogResult {
  entries: ScenarioAuditLogEntry[];
  isLoading: boolean;
  error: string | null;
  /** True when live mode is active — callers use this to decide the data source. */
  isLive: boolean;
}

const EMPTY_ENTRIES: ScenarioAuditLogEntry[] = [];

/** Demo audit events carry a union event type; the log shows a readable title. */
const DEMO_EVENT_TITLES: Record<ScenarioAuditEventType, string> = {
  created: "Scenario created",
  edited: "Scenario updated",
  shared: "Scenario shared",
  unshared: "Scenario unshared",
  "submitted-for-approval": "Scenario sent for approval",
  "approval-granted": "Scenario approved",
  "approval-rejected": "Scenario rejected",
  "approval-withdrawn": "Approval withdrawn",
  "changes-requested": "Changes requested",
  reviewed: "Scenario reviewed",
  committed: "Scenario committed",
  parked: "Scenario parked",
  reactivated: "Scenario unparked",
  archived: "Scenario archived",
  "status-changed": "Status changed",
};

/** Demo event types onto the backend AuditType keys the timeline styles on. */
const DEMO_EVENT_AUDIT_TYPES: Record<ScenarioAuditEventType, string> = {
  created: "System",
  edited: "Adjustment",
  shared: "Shared",
  unshared: "Shared",
  "submitted-for-approval": "SentForApproval",
  "approval-granted": "Approved",
  "approval-rejected": "Rejected",
  "approval-withdrawn": "Withdrawn",
  "changes-requested": "System",
  reviewed: "SentForReview",
  committed: "Approved",
  parked: "Parked",
  reactivated: "Parked",
  archived: "System",
  "status-changed": "System",
};

export const scenarioAuditLogQueryKey = (scenarioId: number) =>
  ["scenarioAuditLog", scenarioId] as const;

/**
 * Map a demo-mode `ScenarioAuditEntry` onto the shape the Audit Log tab renders,
 * so demo and live modes display the same log.
 */
export function mapDemoAuditEntry(
  entry: ScenarioAuditEntry,
): ScenarioAuditLogEntry {
  const description = [
    entry.detail,
    entry.stageName ? `Stage: ${entry.stageName}` : null,
  ]
    .filter(Boolean)
    .join(" — ");

  return {
    id: entry.id,
    auditType: DEMO_EVENT_AUDIT_TYPES[entry.eventType] ?? "System",
    title: DEMO_EVENT_TITLES[entry.eventType] ?? entry.eventType,
    description: description || undefined,
    timestamp: entry.timestamp,
    user: entry.actorName || entry.actorId || undefined,
    oldValues: entry.fromStatus ? { Status: entry.fromStatus } : undefined,
    newValues: entry.toStatus ? { Status: entry.toStatus } : undefined,
  };
}

/**
 * Live-mode data source for the Audit Log tab, reading vw_ScenarioAuditLog via
 * GraphQL. In demo mode this is a no-op and callers map `scenario.auditLog`
 * with `mapDemoAuditEntry`.
 */
export function useScenarioAuditLog(
  scenarioId: number | undefined,
  options?: { enabled?: boolean },
): ScenarioAuditLogResult {
  const { mode } = useApp();
  const isLive = mode === "live";
  const enabled =
    isLive && options?.enabled !== false && typeof scenarioId === "number";

  const listQuery = useQuery({
    queryKey: scenarioAuditLogQueryKey(scenarioId ?? -1),
    queryFn: () => getScenarioAuditLogGraphql(scenarioId as number),
    enabled,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });

  return {
    entries: listQuery.data ?? EMPTY_ENTRIES,
    isLoading: enabled && listQuery.isPending,
    error:
      listQuery.error instanceof Error
        ? listQuery.error.message
        : listQuery.error
          ? "Failed to load the scenario audit log"
          : null,
    isLive,
  };
}
